// StudentEnrolledCourses.js
import React, { useEffect, useState } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import SummaryApi from "../common";
import { useAuth } from '../helpers/AuthContext';
import { useNavigate } from "react-router-dom";
import nocourse from "../asset/nocourse.png";

const StudentEnrolledCourses = () => {
  const { studentId, studentName, studentEmail, authLoaded } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch courses when studentId changes or component mounts
  useEffect(() => {
    console.log("StudentEnrolledCourses: useEffect running. studentId from context:", studentId);

    if (!studentId) {
      console.error("StudentEnrolledCourses: No studentId found in AuthContext. Redirecting to login.");
      setLoading(false);
      navigate("/student-login");
      return;
    }

    fetch(SummaryApi.getCourseByStudentId.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        student_id: studentId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setCourses(data.data);
        } else {
          console.error("Invalid response structure:", data);
        }
      })
      .catch((error) => {
        console.error("Error fetching courses:", error);
      })
      .finally(() => setLoading(false));
  }, [studentId, navigate]);

  // Function to handle course click - navigate to curriculum
  const handleCourseClick = (course) => {
    // Navigate to curriculum page with course_id and time_slot_id
    // Same format as in Dashboard
    navigate(`/student-panel/course-curriculum/${btoa(course.course_id.toString())}?time_slot_id=${btoa(course.time_slot_id.toString())}`);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start py-8 px-4 bg-gray-50 dark:bg-gray-900">
      <h2 className="text-3xl font-bold mb-8 text-[#1f4774] dark:text-white tracking-tight">
        My Courses
      </h2>

      {loading ? (
        <div className="flex justify-center items-center min-h-[60vh]">
          <p className="text-lg text-gray-600 dark:text-gray-300 animate-pulse">Loading courses...</p>
        </div>
      ) : courses.length === 0 ? (
        <div className="flex justify-center items-center w-full min-h-[70vh]">
          <img
            src={nocourse}
            alt="No courses available"
            className="w-60 sm:w-72 md:w-80 lg:w-96 object-contain opacity-90"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full max-w-7xl">
          {courses.map((course, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col h-full cursor-pointer hover:scale-[1.02] transition-transform duration-300"
              onClick={() => handleCourseClick(course)} // Make entire card clickable
            >
              <div className="relative">
                <img
                  src={
                    course?.course_image1
                      ? `${SummaryApi.Images?.url?.replace("api", "")}${course?.course_image1}`
                      : "https://via.placeholder.com/300x160"
                  }
                  alt={course.course_title}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-3 left-3">
                  <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 text-xs font-medium px-2.5 py-1 rounded-full">
                    {course.category_name}
                  </span>
                </div>
              </div>

              <div className="p-5 flex flex-col flex-grow">
                <h3 className="font-bold text-lg text-gray-800 dark:text-white line-clamp-2 mb-2 leading-tight">
                  {course.course_title}
                </h3>

                <div className="flex items-center mb-3">
                  <span className="text-yellow-500 dark:text-yellow-400 font-semibold text-sm flex items-center">
                    ⭐ {course.rating || "N/A"}
                  </span>
                  <span className="text-gray-500 dark:text-white text-xs ml-2">({course.total_reviews || 0} reviews)</span>
                </div>

                <div className="flex items-center text-gray-600 dark:text-white text-sm mb-4">
                  <FaCalendarAlt className="mr-2 text-gray-500 dark:text-white" />
                  <span>{course.course_duration || "Duration not specified"}</span>
                </div>

                <div className="mt-auto">
                  <div className="flex justify-between items-center text-xs text-gray-500 dark:text-white mb-1">
                    <span>Progress</span>
                    <span>0%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-700 dark:from-blue-600 dark:to-blue-800 h-2 rounded-full transition-all duration-500"
                      style={{ width: `0%` }}
                    ></div>
                  </div>
                </div>
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentEnrolledCourses;